import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  LogOut,
  HandCoins,
  Boxes,
  Handshake,
  ShoppingBasket,
  Mail,
  WandSparkles,
  Home,
} from "lucide-react";
import { signOut } from "firebase/auth";
import auth from "../../../firebase.init";
import { useAuthState } from "react-firebase-hooks/auth";
import axios from "axios";

const Sidebar = ({ sidebarCollapsed, toggleSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const checkIfAdmin = async () => {
      if (user?.email) {
        try {
          const res = await fetch("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/users/admins");
          const admins = await res.json();
          const found = admins.some(
            (adminUser) => adminUser.UserEmail === user.email,
          );
          setIsAdmin(found);
        } catch (err) {
          console.error("Error checking admin status:", err);
        }
      }
    };

    checkIfAdmin();
  }, [user]);

  const navItems = [
    {
      path: "/user/dashboard",
      name: "Dashboard",
      icon: <LayoutDashboard size={20} className="me-2" />,
    },
    {
      path: "/user/all-tools",
      name: "All Tool sList",
      icon: <WandSparkles size={20} className="me-2" />,
    },
    {
      path: "/user/select-package",
      name: "Upgrade Package",
      icon: <Boxes size={20} className="me-2" />,
    },
    {
      path: "/user/support",
      name: "Support",
      icon: <Handshake size={20} className="me-2" />,
    },
    {
      path: "/user/pending-payment/",
      name: "Pending Payment",
      icon: <HandCoins size={20} className="me-2" />,
    },
    {
      path: "/user/subscription-history",
      name: "Subscription History",
      icon: <ShoppingBasket size={20} className="me-2" />,
    },
    ...(isAdmin
      ? [
          {
            path: "/admin/dashboard",
            name: "Admin Dashboard",
            icon: <LayoutDashboard size={20} className="me-2" />,
          },
        ]
      : []),
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  };

  // ✅ States for logo
  const [logoData, setLogoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLogoData = async () => {
      try {
        const response = await axios.get("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/nav-logo");
        setLogoData(response.data.data);
      } catch (err) {
        setError("Failed to load logo");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogoData();
  }, []);

  // Render logo with proper null checks
  const renderLogo = (className = "") => {
    if (loading) {
      return <div className={`logo-placeholder ${className}`}>Loading...</div>;
    }

    if (error || !logoData || !logoData.isActive) {
      // Fallback to default logo
      return (
        <img
          src="/assets/img/logo/white-logo-2.svg"
          alt="logo-img"
          className={className}
        />
      );
    }

    return (
      <img
        src={logoData.imageUrl}
        alt={logoData.altText}
        className={className}
      />
    );
  };

  return (
    <div
      className={`col-auto px-0 ${
        sidebarCollapsed ? "col-md-1" : "col-md-3 col-xl-2"
      }`}
      style={{ position: "sticky", top: 0, height: "100vh" }}
    >
      <div
        className="d-flex flex-column h-100"
        style={{ backgroundColor: "#212529" }}
      >
        {/* Sidebar Header */}
        <div className="p-3 border-bottom">
          <div className="d-flex align-items-center justify-content-between">
            <Link to="/" className="text-decoration-none">
            <Home size={30} color="white" />
            </Link>
            <button
              className="btn btn-sm btn-dark"
              onClick={toggleSidebar}
              style={{ cursor: "pointer" }}
            >
              {sidebarCollapsed ? "→" : "←"}
            </button>
          </div>
        </div>

        {/* Scrollable Sidebar Body */}
        <div
          className="flex-grow-1 overflow-auto"
          style={{ scrollbarWidth: "thin" }}
        >
          <ul
            className="nav nav-pills flex-column mb-0 align-items-center align-items-sm-start w-100 py-2"
            id="menu"
          >
            {navItems.map((item, index) => {
              const activeStyle = isActive(item.path)
                ? {
                    backgroundColor: "#495057",
                    borderRadius: "0.375rem",
                    width: "100%",
                  }
                : {};

              return (
                <li key={index} className="w-100">
                  {item.name === "Admin Dashboard" ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-link px-3 py-2 text-white w-100 d-flex align-items-center"
                      style={{ cursor: "pointer", ...activeStyle }}
                      title={sidebarCollapsed ? item.name : ""}
                    >
                      {item.icon}
                      <span
                        className={`ms-1 d-${
                          sidebarCollapsed ? "none" : "inline"
                        }`}
                      >
                        {item.name}
                      </span>
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="nav-link px-3 py-2 text-white w-100 d-flex align-items-center"
                      style={{ cursor: "pointer", ...activeStyle }}
                      title={sidebarCollapsed ? item.name : ""}
                    >
                      {item.icon}
                      <span
                        className={`ms-1 d-${
                          sidebarCollapsed ? "none" : "inline"
                        }`}
                      >
                        {item.name}
                      </span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Sidebar Footer */}
        <div className="border-top p-3 mt-auto">
          <button
            onClick={handleLogout}
            className="nav-link px-2 py-2 text-white w-100 d-flex align-items-center"
            style={{
              cursor: "pointer",
              backgroundColor: "transparent",
              border: "none",
            }}
          >
            <LogOut size={20} className="me-2" />
            <span className={`ms-1 d-${sidebarCollapsed ? "none" : "inline"}`}>
              Logout
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
